/**
 * Portfolio value from a LIVE price.
 *
 * Every value and PnL field the portfolio endpoint returns is priced at
 * REQUEST time (`portfolio/handler.go:462-500`), and the only thing that
 * refetched it was the wallet's own balance push — which the backend emits on
 * an applied transaction and never on a price move. So the numbers froze at
 * the last read: not when you navigated away, but always, for as long as you
 * did not trade.
 *
 * The fix is not a poll. The price is already arriving: the app-wide `trades`
 * channel carries every trade on the platform, and each tick names its token
 * and its price. So the portfolio reprices itself from the tape, locally, with
 * no request at all — and canon keeps owning what a price cannot say: which
 * positions exist, what they cost, what a sale already realized.
 *
 * The arithmetic is deliberately the one the pair Rail already uses and which
 * was verified against the canonical answer on eight real holdings:
 *
 *   value      = balance x price
 *   unrealized = value - remaining cost basis
 *   total      = realized + unrealized
 *
 * Three rules keep it honest, and each has a test:
 *
 *   1. A unit whose price is unknown is NOT updated. Applying a rate in GRAM
 *      and not in USD once put the same position on screen at -1% and -0.5%
 *      simultaneously. USD moves only from a USD price, or from the token's
 *      OWN implied rate — never from a platform average.
 *   2. A position the projector could not account for is left alone. An
 *      unknown balance times a known price is a confident number made of
 *      nothing.
 *   3. Market cap is untouched: it needs total supply, which this endpoint
 *      does not carry, so a price cannot produce it.
 *   4. A unit whose COST is unknown carries no PnL. A positions-reader row
 *      (it states basis_status) values unrealized on the known units the
 *      server names (valued_known_raw), never on the whole balance; without
 *      them it moves its value only. The summary's unrealized is the sum of
 *      the rows' declared unrealized, as both servers compute it.
 */

import type { WalletPortfolio, WireHolding } from '../api/portfolio'
import { plainDecimal } from './format'
import { ratio } from './liveFold'
import { type LivePriceEntry, livePriceUsd, restatedRemainingUsd } from './livePriceStore'
import { grossDivergent } from './markGuard'

/**
 * The stored price, re-exported under the name this module's callers use. It is
 * the STORE's type on purpose: a second shape for the same quantity is how the
 * two screens ended up with two answers in the first place.
 */
export type LivePrice = LivePriceEntry

export type LivePriceMap = Readonly<Record<string, LivePrice>>

function positive(value: number | null | undefined): number | null {
  if (value === null || value === undefined) return null
  return Number.isFinite(value) && value > 0 ? value : null
}

function num(raw: string | undefined): number | null {
  if (typeof raw !== 'string' || raw.trim() === '') return null
  const value = Number(raw)
  return Number.isFinite(value) ? value : null
}

/** Numbers reach the wire as decimal strings; keep that shape so every
    consumer downstream parses one format. `String(n)` becomes `1e-18` below
    1e-6, and the table formatters then treat a known residue as "—". */
function text(value: number): string {
  return plainDecimal(value)
}

type Repriced = { holding: WireHolding; valueTon: number | null; valueUsd: number | null }

/** A positions-reader row (WT_POSITIONS_READ_PORTFOLIO): it states
    basis_status. Its balance can hold units of unknown cost (by transfer,
    before the tape), and its remaining basis is the KNOWN units' ("0" when
    none), so value − basis would read their whole value as profit. */
function positionsRow(holding: WireHolding): boolean {
  const status = holding.basis_status
  return typeof status === 'string' && status !== ''
}

/** The known units a positions row's unrealized is valued on, in whole
    tokens (valued_known_raw / 10^token_decimals); null when the row states
    none — no position, unknown cost (basis_unknown / held_basis_unknown),
    unknown decimals, or a server that does not send them. */
const WITHHELD_REASONS = ['held_basis_unknown', 'basis_unknown']

/** The server withheld this row's PnL in this unit: a basis reason, or a
    priced row with no total in the unit (a TON-pure row with no TON mark).
    The one predicate the portfolio (repriceRow) and the pair page
    (PositionActivity) both use, so the two screens agree on "—". */
export function serverWithheldPnl(holding: WireHolding, unit: 'gram' | 'usd'): boolean {
  if (holding.pnl_reasons?.some((reason) => WITHHELD_REASONS.includes(reason))) return true
  return (
    holding.price_available &&
    num(unit === 'gram' ? holding.total_pnl_ton : holding.total_pnl_usd) === null
  )
}

export function knownUnits(
  holding: WireHolding,
  field: 'valued_known_raw' | 'valued_known_ton_raw' = 'valued_known_raw'
): number | null {
  const raw = holding[field]
  const dec = holding.token_decimals
  if (typeof raw !== 'string' || !/^(0|[1-9]\d*)$/.test(raw)) return null
  if (typeof dec !== 'number' || !Number.isInteger(dec) || dec < 0 || dec > 255) return null
  return ratio(BigInt(raw), 10n ** BigInt(dec))
}

/**
 * The last live price each row TOOK, by token and mark pool (review M2): the
 * reference the ×50 band judges the next tick against, so a real trend is
 * followed past 50× of the REST snapshot while one absurd tick is still
 * dropped. Owned by the caller (one map for every portfolio it reprices);
 * without it every tick is judged against the snapshot.
 */
export type MarkAnchors = Map<string, LivePrice>

function anchorKey(holding: WireHolding): string {
  return `${holding.token_master}\n${holding.mark_pool ?? ''}`
}

/** The live price against a reference, in the unit both state (USD first). */
function outsideBand(live: LivePrice, refUsd: number | null, refTon: number | null): boolean {
  const liveUsd = livePriceUsd(live)
  if (refUsd !== null && liveUsd !== null) return grossDivergent(liveUsd, refUsd)
  if (refTon !== null) return grossDivergent(live.priceTon, refTon)
  return false
}

/**
 * The live price a row is valued at, or undefined (the server's figures
 * stand). A tick is the row's to take when it comes from its mark pool
 * (review F3 — another pool's tick, a drained pool's dust print, a
 * stableswap is not its price; a row with no mark pool takes none) and lies
 * within ~50× of the row's reference (review N2 — beyond it a print is decode
 * garbage, not a move; lib/markGuard, the server's quotepool.MarkPrint). The
 * reference is the last price the row took (anchors, review M2), else the mark
 * the row was read with. A tick not taken leaves the row at its last taken
 * price — never back to the older snapshot. A drained pool's row (mark_source
 * 'reserve') takes none: it is worth what the pool pays now, per holding, and a
 * tick from that pool is a pre-drain print (operator rule 2026-11-03).
 */
function rowPrice(
  holding: WireHolding,
  live: LivePrice | undefined,
  anchors: MarkAnchors | undefined,
  snapshotAtMs: number | undefined
): LivePrice | undefined {
  if (!holding.mark_pool || holding.mark_source === 'reserve') return undefined
  const key = anchorKey(holding)
  let anchor = anchors?.get(key)
  if (anchor && snapshotAtMs !== undefined && anchor.knownAsOfMs < snapshotAtMs) {
    // Heard before the read the row comes from was requested: the server's
    // figure is the newer knowledge — for THIS view the anchor is absent and
    // the snapshot stands. It is not deleted: the map serves views read at
    // other moments (canon, the selected wallet, its parts — review of
    // 898c684fd #4), and for an older view it is still the newer knowledge.
    anchor = undefined
  }
  if (!live || live.pool !== holding.mark_pool) return anchor
  const outside = anchor
    ? outsideBand(live, livePriceUsd(anchor), anchor.priceTon)
    : outsideBand(live, num(holding.current_price_usd), num(holding.current_price_ton))
  if (outside) return anchor
  anchors?.set(key, live)
  return live
}

function repriceHolding(holding: WireHolding, live: LivePrice | undefined): Repriced {
  const untouched: Repriced = {
    holding,
    valueTon: num(holding.current_value_ton),
    valueUsd: num(holding.current_value_usd),
  }
  if (!live) return untouched
  // A position whose history the projector refused to account for carries no
  // figures at all. It must not gain one here.
  if (holding.position_unavailable) return untouched

  const balance = num(holding.balance)
  if (balance === null || balance < 0) return untouched

  const priceTon = positive(live.priceTon)
  if (priceTon === null) return untouched

  // USD only from a USD price or the token's OWN rate — the store's rule, used
  // by both screens so one price cannot produce two USD numbers.
  const priceUsd = livePriceUsd(live)

  const next: WireHolding = { ...holding }
  const valueTon = balance * priceTon
  next.current_price_ton = text(priceTon)
  next.current_value_ton = text(valueTon)
  const basisTon = num(holding.remaining_cost_basis_ton)
  const realizedTon = num(holding.realized_pnl_ton)
  if (basisTon !== null) {
    const unrealized = valueTon - basisTon
    next.unrealized_pnl_ton = text(unrealized)
    if (realizedTon !== null) next.total_pnl_ton = text(realizedTon + unrealized)
  }

  let valueUsd = num(holding.current_value_usd)
  if (priceUsd !== null) {
    valueUsd = balance * priceUsd
    next.current_price_usd = text(priceUsd)
    next.current_value_usd = text(valueUsd)
    const basisUsd =
      restatedRemainingUsd(num(holding.remaining_cost_basis_ton), priceTon, priceUsd) ??
      num(holding.remaining_cost_basis_usd)
    const realizedUsd = num(holding.realized_pnl_usd)
    if (basisUsd !== null) {
      next.remaining_cost_basis_usd = text(basisUsd)
      const unrealized = valueUsd - basisUsd
      next.unrealized_pnl_usd = text(unrealized)
      if (realizedUsd !== null) next.total_pnl_usd = text(realizedUsd + unrealized)
    }
  }

  // A token canon could not price is priceable now: the tape price is a real
  // trade on chain, and refusing to state a value we can compute is the same
  // mistake as rendering a known number as an em-dash.
  if (!next.price_available) next.price_available = true

  return { holding: next, valueTon, valueUsd }
}

/**
 * A row at a live price. A positions row moves its value on every unit, but
 * its PnL only on the known units the server valued (the server's own
 * formula: known × mark − known basis); without them, its PnL fields stay as
 * the server sent them. Any other row reprices as before.
 */
function repriceRow(holding: WireHolding, live: LivePrice | undefined): Repriced {
  const out = repriceHolding(holding, live)
  if (out.holding === holding || !live || !positionsRow(holding)) return out
  const next: WireHolding = {
    ...out.holding,
    remaining_cost_basis_usd: holding.remaining_cost_basis_usd,
    unrealized_pnl_ton: holding.unrealized_pnl_ton,
    total_pnl_ton: holding.total_pnl_ton,
    unrealized_pnl_usd: holding.unrealized_pnl_usd,
    total_pnl_usd: holding.total_pnl_usd,
  }
  const known = knownUnits(holding)
  const priceUsd = livePriceUsd(live)
  const basisUsd = num(holding.remaining_cost_basis_usd)
  if (known !== null && priceUsd !== null && basisUsd !== null) {
    const unrealized = known * priceUsd - basisUsd
    next.unrealized_pnl_usd = text(unrealized)
    const realizedUsd = num(holding.realized_pnl_usd)
    if (realizedUsd !== null) next.total_pnl_usd = text(realizedUsd + unrealized)
  }
  // GRAM on the TON-known units (valued_known_ton_raw), as the pair page does —
  // and, like it, not where the server withheld the GRAM PnL (one predicate).
  const knownTon = knownUnits(holding, 'valued_known_ton_raw')
  const priceTon = positive(live.priceTon)
  const basisTon = num(holding.remaining_cost_basis_ton)
  if (
    knownTon !== null &&
    priceTon !== null &&
    basisTon !== null &&
    !serverWithheldPnl(holding, 'gram')
  ) {
    const unrealized = knownTon * priceTon - basisTon
    next.unrealized_pnl_ton = text(unrealized)
    const realizedTon = num(holding.realized_pnl_ton)
    if (realizedTon !== null) next.total_pnl_ton = text(realizedTon + unrealized)
  }
  return { ...out, holding: next }
}

function sum(values: (number | null)[]): number | null {
  let total = 0
  let seen = false
  for (const value of values) {
    if (value === null) continue
    total += value
    seen = true
  }
  return seen ? total : null
}

/**
 * Returns the portfolio with every held token repriced from the tape. The
 * input is never mutated, and a portfolio with nothing to reprice comes back
 * as the same object so consumers keep their referential equality.
 */
export function applyLivePricesToPortfolio(
  portfolio: WalletPortfolio | null,
  prices: LivePriceMap,
  /** The rows' last taken live prices (review M2); read and updated here. */
  anchors?: MarkAnchors,
  /** When the read this portfolio comes from was requested (wall ms): an
      anchor heard before it is reset (the snapshot is newer knowledge). */
  snapshotAtMs?: number | null
): WalletPortfolio | null {
  // A portfolio with no holdings array at all is not a shape this can reprice.
  // It reaches here from stubs and from any future answer that omits the list;
  // either way there is nothing to multiply, and throwing would take the whole
  // provider — and with it every trading surface — down with it.
  if (!portfolio || !Array.isArray(portfolio.holdings) || portfolio.holdings.length === 0) {
    return portfolio
  }

  const repriced = portfolio.holdings.map((holding) =>
    repriceRow(
      holding,
      rowPrice(holding, prices[holding.token_master], anchors, snapshotAtMs ?? undefined)
    )
  )
  if (repriced.every((entry, index) => entry.holding === portfolio.holdings[index])) {
    return portfolio
  }

  const next: WalletPortfolio = { ...portfolio, holdings: repriced.map((entry) => entry.holding) }

  // The summary must move with the rows: a total that disagrees with the table
  // under it is worse than a stale one, because nothing on screen says which
  // of the two to believe.
  const valueTon = sum(repriced.map((entry) => entry.valueTon))
  const valueUsd = sum(repriced.map((entry) => entry.valueUsd))
  // Unrealized is the sum of the rows' DECLARED unrealized — the server's
  // own rule (both readers sum the rows'): a row with none (no position, no
  // basis, unknown cost) adds nothing, never its value as profit.
  const unrealizedTon = sum(next.holdings.map((h) => num(h.unrealized_pnl_ton)))
  const unrealizedUsd = sum(next.holdings.map((h) => num(h.unrealized_pnl_usd)))
  const known = summaryKnown(portfolio)
  const usd = restated(known, valueUsd, unrealizedUsd, num(portfolio.total_realized_pnl_usd), {
    value: portfolio.total_current_value_usd,
    unrealized: portfolio.total_unrealized_pnl_usd,
  })
  if (usd.value != null) next.total_current_value_usd = usd.value
  if (usd.unrealized != null) next.total_unrealized_pnl_usd = usd.unrealized
  if (usd.total != null) next.total_pnl_usd = usd.total
  const rate = num(portfolio.ton_usd_rate)
  if (rate !== null && rate > 0) {
    // The positions read states its GRAM totals as its USD ones at TON/USD
    // (never a sum of the rows' GRAM figures, which only TON-pure positions
    // carry): restate them the same way, from the restated dollars.
    const atRate = (v: string | null) => (v === null ? null : text(Number(v) / rate))
    const value = atRate(usd.value)
    const unrealized = atRate(usd.unrealized)
    const total = atRate(usd.total)
    if (value != null) next.total_current_value_ton = value
    if (unrealized != null) next.total_unrealized_pnl_ton = unrealized
    if (total != null) next.total_pnl_ton = total
    return next
  }
  // The positions read with no rate stated its GRAM totals "—" (rate unread):
  // a sum of its rows' GRAM would be a partial figure shown as the total.
  if (portfolio.positions_source === 'wallet_token_positions') return next
  const ton = restated(known, valueTon, unrealizedTon, num(portfolio.total_realized_pnl_ton), {
    value: portfolio.total_current_value_ton,
    unrealized: portfolio.total_unrealized_pnl_ton,
  })
  if (ton.value != null) next.total_current_value_ton = ton.value
  if (ton.unrealized != null) next.total_unrealized_pnl_ton = ton.unrealized
  if (ton.total != null) next.total_pnl_ton = ton.total
  return next
}

/** One unit's summary restated from the rows; null = leave the server's. */
function restated(
  known: ReturnType<typeof summaryKnown>,
  value: number | null,
  unrealized: number | null,
  realized: number | null,
  sent: { value: string | undefined; unrealized: string | undefined }
): { value: string | null; unrealized: string | null; total: string | null } {
  const out = { value: null, unrealized: null, total: null } as {
    value: string | null
    unrealized: string | null
    total: string | null
  }
  if (value === null || !known.value(sent.value)) return out
  out.value = text(value)
  if (unrealized === null || !known.pnl(sent.unrealized)) return out
  out.unrealized = text(unrealized)
  if (realized !== null) out.total = text(realized + unrealized)
  return out
}

/**
 * Whether a summary figure may be restated from the rows — the server's rule
 * (positions_read.go portfolioFromHoldings, operator 2026-11-03, review F4):
 * a total is the sum of every row that has the figure. A row with no market
 * (price_unavailable_reason no_market) is "—" and left out of the sum; it
 * never turns the total into "—". A row whose price was not read (unread)
 * makes the value "—": the server then says price_coverage_complete false,
 * and the value is not restated. The PnL totals sum the stated rows. A read
 * whose positions store did not answer (backfill_status "unavailable")
 * states no total, and its "" stays.
 */
function summaryKnown(p: WalletPortfolio) {
  const storeOk = p.backfill_status !== 'unavailable'
  const valueOk = storeOk && p.price_coverage_complete !== false
  return {
    value: (_v: string | undefined) => valueOk,
    pnl: (_v: string | undefined) => storeOk,
  }
}
